import React, { useEffect, useMemo, useState } from 'react';
import { TbAdjustmentsHorizontal, TbCheck } from 'react-icons/tb';
import BottomSheet from './BottomSheet';

export const CATEGORY_CHIP_BRAND = '#FF385C';
const MOBILE_BREAKPOINT = 768;
const DEFAULT_DESKTOP_LIMIT = 8;

const STYLES = {
  light: {
    bar: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      padding: '0 1rem 1rem',
    },
    btn: {
      minHeight: 44,
      padding: '0.5rem 1rem',
      borderRadius: 9999,
      background: '#f1f5f9',
      border: '1px solid #e5e7eb',
      color: '#334155',
      fontWeight: 500,
      fontSize: '0.875rem',
      cursor: 'pointer',
      transition: 'all 0.15s',
      whiteSpace: 'nowrap',
    },
    btnActive: {
      background: CATEGORY_CHIP_BRAND,
      color: '#fff',
      borderColor: CATEGORY_CHIP_BRAND,
    },
    mobileRow: {
      padding: '0 1rem 1rem',
    },
    mobileFilter: {
      background: '#fff',
      color: '#334155',
      border: '1px solid #e5e7eb',
    },
    mobileChip: {
      background: '#f8fafc',
      color: '#334155',
      border: '1px solid #e5e7eb',
    },
  },
  dark: {
    bar: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      flexWrap: 'wrap',
      padding: '0.5rem 0',
    },
    btn: {
      minHeight: 44,
      padding: '0.8rem 1.5rem',
      borderRadius: 25,
      border: '1px solid rgba(255,255,255,0.3)',
      background: 'rgba(255,255,255,0.1)',
      backdropFilter: 'blur(10px)',
      color: '#fff',
      fontWeight: 500,
      fontSize: '0.9rem',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    btnActive: {
      background: 'rgba(255,56,92,0.8)',
      color: '#fff',
      borderColor: 'rgba(255,56,92,0.8)',
    },
    mobileRow: {
      justifyContent: 'center',
      padding: '0.5rem 0',
    },
    mobileFilter: {
      background: 'rgba(255,255,255,0.12)',
      color: '#fff',
      border: '1px solid rgba(255,255,255,0.3)',
    },
    mobileChip: {
      background: 'rgba(255,255,255,0.16)',
      color: '#fff',
      border: '1px solid rgba(255,255,255,0.28)',
    },
  },
};

/**
 * Presentational filter pill strip. Filter state ownership (URL vs useState)
 * lives in the parent — this component is agnostic.
 *
 * @param {{
 *   categories: Array<{ slug: string|null, name: string, count?: number }>,
 *   activeSlug: string|null,
 *   onChange: (slug: string|null) => void,
 *   allLabel?: string,
 *   allCount?: number,
 *   variant?: 'light'|'dark',
 *   includeAll?: boolean,
 *   className?: string,
 *   barStyle?: React.CSSProperties,
 *   trailingItems?: Array<{ key: string, name: string, count?: number, onClick: () => void, active?: boolean, dashed?: boolean }>,
 *   mobileCollapseStrategy?: 'sheet'|'none',
 *   desktopCollapseThreshold?: number,
 *   resultLabel?: string,
 *   sheetTitle?: string,
 * }} props
 */
export default function CategoryFilterPills({
  categories,
  activeSlug = null,
  onChange,
  allLabel = 'All',
  allCount,
  variant = 'light',
  includeAll = true,
  className = '',
  barStyle = {},
  trailingItems = [],
  mobileCollapseStrategy = 'sheet',
  desktopCollapseThreshold = DEFAULT_DESKTOP_LIMIT,
  resultLabel = 'items',
  sheetTitle = 'Filter by category',
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [desktopExpanded, setDesktopExpanded] = useState(false);
  const s = STYLES[variant] || STYLES.light;
  const items = useMemo(
    () => includeAll
      ? [{ slug: null, name: allLabel, count: allCount }, ...categories]
      : categories,
    [allCount, allLabel, categories, includeAll]
  );
  const activeItem = items.find((item) => (item.slug ?? null) === (activeSlug ?? null));
  const activeCategory = activeSlug ? activeItem : null;
  const hasActiveFilter = !!activeCategory;
  const mobileSheetEnabled = mobileCollapseStrategy === 'sheet';
  const shouldCollapseDesktop = items.length > desktopCollapseThreshold;
  const visibleDesktopItems = useMemo(() => {
    if (!shouldCollapseDesktop || desktopExpanded) return items;

    const visible = items.slice(0, desktopCollapseThreshold);
    const activeIndex = items.findIndex((item) => (item.slug ?? null) === (activeSlug ?? null));

    if (activeIndex >= desktopCollapseThreshold && activeIndex !== -1 && desktopCollapseThreshold > 0) {
      return [...items.slice(0, desktopCollapseThreshold - 1), items[activeIndex]];
    }

    return visible;
  }, [activeSlug, desktopCollapseThreshold, desktopExpanded, items, shouldCollapseDesktop]);
  const hiddenDesktopCount = Math.max(items.length - visibleDesktopItems.length, 0);
  const showButtonLabel = activeItem && typeof activeItem.count === 'number'
    ? `Show ${activeItem.count} ${resultLabel}`
    : `Show all ${resultLabel}`;

  useEffect(() => {
    if (!sheetOpen || typeof window === 'undefined') return undefined;

    const media = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT + 1}px)`);
    const closeOnDesktop = (event) => {
      if (event.matches) setSheetOpen(false);
    };

    if (media.matches) setSheetOpen(false);
    if (media.addEventListener) {
      media.addEventListener('change', closeOnDesktop);
    } else {
      media.addListener(closeOnDesktop);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', closeOnDesktop);
      } else {
        media.removeListener(closeOnDesktop);
      }
    };
  }, [sheetOpen]);

  const formatLabel = (item) => (
    <>
      {item.name}
      {typeof item.count === 'number' ? ` (${item.count})` : ''}
    </>
  );

  const handleSelect = (slug, item) => {
    onChange(slug, item);
  };

  const handleSheetSelect = (item) => {
    handleSelect(item.slug ?? null, item);
    setSheetOpen(false);
  };

  const renderDesktopButton = (item) => {
    const active = (activeSlug ?? null) === (item.slug ?? null);
    return (
      <button
        key={item.slug ?? '__all'}
        type="button"
        style={{ ...s.btn, ...(active ? s.btnActive : {}) }}
        onClick={() => handleSelect(item.slug ?? null, item)}
      >
        {formatLabel(item)}
      </button>
    );
  };

  return (
    <div className={`cfp-root ${mobileSheetEnabled ? '' : 'cfp-no-mobile-collapse'}`}>
      <div className={`cfp-desktop ${className}`} style={{ ...s.bar, ...barStyle }}>
        {visibleDesktopItems.map(renderDesktopButton)}
        {shouldCollapseDesktop && (
          <button
            type="button"
            style={{ ...s.btn, borderStyle: 'dashed' }}
            onClick={() => setDesktopExpanded((current) => !current)}
          >
            {desktopExpanded ? 'Show fewer' : `+ ${hiddenDesktopCount} more`}
          </button>
        )}
        {trailingItems.map((item) => (
          <button
            key={item.key}
            type="button"
            style={{
              ...s.btn,
              ...(item.active ? s.btnActive : {}),
              ...(item.dashed && !item.active ? { borderStyle: 'dashed', background: '#fff' } : {}),
            }}
            onClick={item.onClick}
          >
            {item.name}
            {typeof item.count === 'number' ? ` (${item.count})` : ''}
          </button>
        ))}
      </div>

      {mobileSheetEnabled && (
        <>
          <div className="cfp-mobile" style={s.mobileRow}>
            <button
              type="button"
              className={`cfp-mobile-filter ${hasActiveFilter ? 'is-active' : ''}`}
              style={hasActiveFilter ? undefined : s.mobileFilter}
              aria-haspopup="dialog"
              aria-expanded={sheetOpen}
              onClick={() => setSheetOpen(true)}
            >
              <TbAdjustmentsHorizontal aria-hidden="true" />
              <span>Filter</span>
              {hasActiveFilter && <span className="cfp-filter-dot" aria-hidden="true" />}
            </button>

            {activeCategory && (
              <button
                type="button"
                className="cfp-active-chip"
                style={s.mobileChip}
                onClick={() => handleSelect(null, items[0])}
                aria-label={`Clear ${activeCategory.name} filter`}
              >
                <span>{formatLabel(activeCategory)}</span>
                <span aria-hidden="true">x</span>
              </button>
            )}
          </div>

          <BottomSheet
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            title={sheetTitle}
            footer={
              <button type="button" className="cfp-show-button" onClick={() => setSheetOpen(false)}>
                {showButtonLabel}
              </button>
            }
          >
            <div className="cfp-sheet-list">
              {hasActiveFilter && includeAll && (
                <button type="button" className="cfp-clear-row" onClick={() => handleSheetSelect(items[0])}>
                  Show all
                </button>
              )}
              {items.map((item) => {
                const active = (activeSlug ?? null) === (item.slug ?? null);
                return (
                  <button
                    key={item.slug ?? '__all'}
                    type="button"
                    className={`cfp-sheet-row ${active ? 'is-active' : ''}`}
                    onClick={() => handleSheetSelect(item)}
                  >
                    <span className="cfp-sheet-name">{item.name}</span>
                    <span className="cfp-sheet-meta">
                      {typeof item.count === 'number' && <span>{item.count}</span>}
                      {active && <TbCheck className="cfp-sheet-check" aria-hidden="true" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </BottomSheet>
        </>
      )}

      <style>{`
        .cfp-desktop {
          display: flex;
        }

        .cfp-mobile {
          display: none;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: nowrap;
          overflow: hidden;
        }

        .cfp-mobile-filter {
          min-height: 42px;
          padding: 0.55rem 0.95rem;
          border-radius: 9999px;
          font-weight: 700;
          font-size: 0.9rem;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          flex: 0 0 auto;
          cursor: pointer;
          position: relative;
        }

        .cfp-mobile-filter.is-active {
          background: ${CATEGORY_CHIP_BRAND};
          border: 1px solid ${CATEGORY_CHIP_BRAND};
          color: #fff;
        }

        .cfp-filter-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
        }

        .cfp-active-chip {
          min-width: 0;
          min-height: 42px;
          padding: 0.55rem 0.75rem 0.55rem 0.9rem;
          border-radius: 9999px;
          font-weight: 600;
          font-size: 0.875rem;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          cursor: pointer;
          flex: 1 1 auto;
          max-width: 100%;
        }

        .cfp-active-chip span:first-child {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .cfp-active-chip span:last-child {
          flex: 0 0 auto;
          font-size: 1.1rem;
          line-height: 1;
        }

        .cfp-sheet-list {
          padding: 0.5rem 0;
        }

        .cfp-clear-row {
          width: calc(100% - 2rem);
          margin: 0.5rem 1rem;
          min-height: 42px;
          border-radius: 9999px;
          border: 1px solid #fecdd3;
          background: #fff1f2;
          color: ${CATEGORY_CHIP_BRAND};
          font-weight: 700;
          cursor: pointer;
        }

        .cfp-sheet-row {
          width: 100%;
          min-height: 54px;
          padding: 0.85rem 1rem;
          border: 0;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          color: #111827;
          text-align: left;
          cursor: pointer;
        }

        .cfp-sheet-row.is-active {
          background: #fff1f2;
        }

        .cfp-sheet-name {
          min-width: 0;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .cfp-sheet-meta {
          display: inline-flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.6rem;
          color: #64748b;
          font-weight: 700;
          flex: 0 0 auto;
        }

        .cfp-sheet-check {
          color: ${CATEGORY_CHIP_BRAND};
          width: 1.25rem;
          height: 1.25rem;
        }

        .cfp-show-button {
          width: 100%;
          min-height: 48px;
          border: 0;
          border-radius: 9999px;
          background: ${CATEGORY_CHIP_BRAND};
          color: #fff;
          font-weight: 800;
          font-size: 0.95rem;
          cursor: pointer;
        }

        @media (max-width: ${MOBILE_BREAKPOINT}px) {
          .cfp-root:not(.cfp-no-mobile-collapse) .cfp-desktop {
            display: none !important;
          }

          .cfp-root:not(.cfp-no-mobile-collapse) .cfp-mobile {
            display: flex;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .cfp-mobile-filter,
          .cfp-active-chip,
          .cfp-show-button {
            transition-duration: 1ms !important;
          }
        }
      `}</style>
    </div>
  );
}
